import { useEffect } from "react";
import { Provider } from "react-redux";

import { store, useAppDispatch, useAppSelector } from "@/app/store";
import { TooltipProvider } from "@/shared/ui/components/custom";
import {
  initializeUI,
  setIsMobile,
  selectLanguage,
  selectTheme,
} from "@/app/store/slices/ui.slice";
import { useMediaQuery } from "@/shared/hooks";
import i18n from "@/app/i18n";
import { Toaster as SonnerToaster } from "@/shared/ui/components/ui/Sonner";

// Component to handle UI initialization
function UIInitializer({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const language = useAppSelector(selectLanguage);
  useAppSelector(selectTheme); // Ensures theme state is tracked
  const isMobile = !useMediaQuery("(min-width: 1024px)");

  // Initialize UI on mount and sync i18n with Redux language
  useEffect(() => {
    dispatch(initializeUI());
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [dispatch, language]);

  // Handle responsive sidebar
  useEffect(() => {
    dispatch(setIsMobile(isMobile));
  }, [isMobile, dispatch]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      dispatch(setIsMobile(mobile));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  return <>{children}</>;
}

// Wrapper component for Redux Provider
function ReduxProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider store={store}>
      <UIInitializer>{children}</UIInitializer>
    </Provider>
  );
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  return (
    <ReduxProvider>
      <TooltipProvider delayDuration={200}>{children}</TooltipProvider>
      <SonnerToaster />
    </ReduxProvider>
  );
}
